import { useCallback, useEffect, useRef, useState } from 'react';
import { api, extractError } from '@/lib/api.js';
import { DURACAO_DA_FATIA_S, relogio } from '@shared/conselheiro.js';

/**
 * A gravação da manhã pelo próprio app, em fatias.
 *
 * Cada fatia é um arquivo de áudio completo: o gravador é reiniciado a cada
 * DURACAO_DA_FATIA_S e a fatia que termina sobe direto para o bucket privado
 * (URL assinada pelo HackOS) enquanto a próxima já está gravando. Se o
 * aparelho travar, perde-se no máximo a fatia em curso.
 *
 * "Fora da gravação" para o gravador; o servidor guarda só o início e o fim do
 * trecho, e nada dele sai do navegador.
 */

export type EstadoDoGravador = 'parado' | 'gravando' | 'fora' | 'sem_microfone';

type Fatia = { ordem: number; inicio: Date; fim: Date; blob: Blob };
type Inicio = { arquivo: { id: string }; envio: { url: string; cabecalhos: Record<string, string> } };

/** O formato que este navegador grava: webm/opus no Chrome e Firefox, mp4 no Safari. */
function formatoSuportado(): string {
  const opcoes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return opcoes.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) ?? '';
}

/** Duração da fatia; dá para encurtar no navegador, para testar (hackacademy_fatia_s). */
function duracaoDaFatiaMs(): number {
  try {
    const n = Number(localStorage.getItem('hackacademy_fatia_s'));
    if (Number.isFinite(n) && n >= 5) return Math.min(n, DURACAO_DA_FATIA_S) * 1000;
  } catch {
    /* sem armazenamento: vale o padrão */
  }
  return DURACAO_DA_FATIA_S * 1000;
}

const espera = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useGravador(manhaId: string, proximaOrdem: number, aoMudar: () => void) {
  const [estado, setEstado] = useState<EstadoDoGravador>('parado');
  const [pendentes, setPendentes] = useState(0);
  const [enviadas, setEnviadas] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [inicioDaFatia, setInicioDaFatia] = useState<number | null>(null);

  const stream = useRef<MediaStream | null>(null);
  const gravador = useRef<MediaRecorder | null>(null);
  const relogioDaFatia = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ordem = useRef(proximaOrdem);
  const fila = useRef<Promise<void>>(Promise.resolve());
  const tela = useRef<{ release: () => Promise<void> } | null>(null);
  const aoMudarRef = useRef(aoMudar);
  aoMudarRef.current = aoMudar;

  useEffect(() => {
    // A ordem só avança: o servidor sabe das fatias já enviadas antes de recarregar.
    ordem.current = Math.max(ordem.current, proximaOrdem);
  }, [proximaOrdem]);

  /** Envia uma fatia: pede a URL, manda o áudio, confirma. Tenta de novo se a rede falhar. */
  const enviar = useCallback(
    async (f: Fatia) => {
      for (let tentativa = 1; ; tentativa++) {
        try {
          const { data } = await api.post<Inicio>(`/conselheiro/manhas/${manhaId}/gravacao/fatias`, {
            ordem: f.ordem,
            inicio_em: f.inicio.toISOString(),
            fim_em: f.fim.toISOString(),
            tipo: f.blob.type || 'audio/webm',
            tamanho: f.blob.size,
          });
          const put = await fetch(data.envio.url, { method: 'PUT', headers: data.envio.cabecalhos, body: f.blob });
          if (!put.ok) throw new Error(`O armazenamento recusou a fatia (${put.status}).`);
          await api.post(`/conselheiro/anexos/${data.arquivo.id}/concluir`);
          setEnviadas((n) => n + 1);
          aoMudarRef.current();
          return;
        } catch (e) {
          const recusa = (e as { response?: { status?: number } }).response?.status;
          // Recusa do servidor (consentimento, manhã encerrada há muito) não melhora tentando de novo.
          if ((recusa && recusa < 500) || tentativa >= 4) {
            setErro(`A fatia ${f.ordem} não foi guardada: ${extractError(e).message}`);
            return;
          }
          await espera(2000 * 2 ** tentativa);
        }
      }
    },
    [manhaId],
  );

  const enfileirar = useCallback(
    (f: Fatia) => {
      setPendentes((n) => n + 1);
      fila.current = fila.current.then(() => enviar(f)).finally(() => setPendentes((n) => n - 1));
    },
    [enviar],
  );

  /** Começa uma fatia nova no stream aberto; a anterior já foi entregue à fila. */
  const comecarFatia = useCallback(() => {
    if (!stream.current) return;
    const tipo = formatoSuportado();
    const rec = new MediaRecorder(stream.current, { ...(tipo ? { mimeType: tipo } : {}), audioBitsPerSecond: 48_000 });
    const pedacos: Blob[] = [];
    const inicio = new Date();
    const minhaOrdem = ordem.current++;
    rec.ondataavailable = (e) => e.data.size > 0 && pedacos.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(pedacos, { type: rec.mimeType || tipo || 'audio/webm' });
      if (blob.size > 0) enfileirar({ ordem: minhaOrdem, inicio, fim: new Date(), blob });
    };
    rec.start(10_000);
    gravador.current = rec;
    setInicioDaFatia(inicio.getTime());
    relogioDaFatia.current = setTimeout(() => {
      // Fecha esta fatia e abre a próxima: cada uma é um arquivo completo.
      rec.stop();
      comecarFatia();
    }, duracaoDaFatiaMs());
  }, [enfileirar]);

  const fecharFatia = useCallback(() => {
    if (relogioDaFatia.current) clearTimeout(relogioDaFatia.current);
    relogioDaFatia.current = null;
    if (gravador.current && gravador.current.state !== 'inactive') gravador.current.stop();
    gravador.current = null;
    setInicioDaFatia(null);
  }, []);

  const soltarMicrofone = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    void tela.current?.release().catch(() => undefined);
    tela.current = null;
  }, []);

  const gravar = useCallback(async () => {
    setErro(null);
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      setEstado('sem_microfone');
      setErro('O navegador não liberou o microfone. Autorize o microfone para este site e tente de novo.');
      return;
    }
    // Três horas de gravação: a tela não pode apagar.
    try {
      const wl = (
        navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }
      ).wakeLock;
      tela.current = (await wl?.request('screen')) ?? null;
    } catch {
      /* sem bloqueio de tela: segue gravando */
    }
    comecarFatia();
    setEstado('gravando');
  }, [comecarFatia]);

  /** Para tudo e espera as fatias pendentes subirem. */
  const parar = useCallback(async () => {
    fecharFatia();
    soltarMicrofone();
    setEstado('parado');
    await espera(50); // o onstop da última fatia entra na fila
    await fila.current;
  }, [fecharFatia, soltarMicrofone]);

  const sairDaGravacao = useCallback(async () => {
    fecharFatia();
    setEstado('fora');
    try {
      await api.post(`/conselheiro/manhas/${manhaId}/gravacao/fora`, { entrar: true });
    } catch (e) {
      setErro(extractError(e).message);
    }
    aoMudarRef.current();
  }, [fecharFatia, manhaId]);

  const voltarAGravar = useCallback(async () => {
    try {
      await api.post(`/conselheiro/manhas/${manhaId}/gravacao/fora`, { entrar: false });
    } catch (e) {
      setErro(extractError(e).message);
      return;
    }
    if (!stream.current) {
      await gravar();
    } else {
      comecarFatia();
      setEstado('gravando');
    }
    aoMudarRef.current();
  }, [manhaId, gravar, comecarFatia]);

  // Fechar a aba no meio perderia a fatia em curso e as que ainda estão subindo.
  useEffect(() => {
    const avisar = (e: BeforeUnloadEvent) => {
      if (estado === 'gravando' || pendentes > 0) e.preventDefault();
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [estado, pendentes]);

  // Sair da página para o gravador e solta o microfone.
  useEffect(
    () => () => {
      fecharFatia();
      soltarMicrofone();
    },
    [fecharFatia, soltarMicrofone],
  );

  return {
    estado,
    pendentes,
    enviadas,
    erro,
    inicioDaFatia,
    gravar,
    parar,
    sairDaGravacao,
    voltarAGravar,
    suportado: !!formatoSuportado(),
  };
}

export type Gravador = ReturnType<typeof useGravador>;

/** O painel da gravação, para o Principal. */
export function PainelDaGravacao({
  g,
  gravacao,
  agora,
}: {
  g: Gravador;
  gravacao: {
    pode: boolean;
    faltam: string[];
    armazenamento: boolean;
    segundos: number;
    fatias: number;
    fora_da_gravacao: boolean;
  };
  agora: number;
}) {
  const gravando = g.estado === 'gravando';
  const naFatia = g.inicioDaFatia ? (agora - g.inicioDaFatia) / 1000 : 0;
  const bloqueio = !gravacao.armazenamento
    ? 'O armazenamento das gravações ainda não foi configurado no HackOS.'
    : !g.suportado
      ? 'Este navegador não grava áudio. Use o Chrome, o Edge, o Firefox ou o Safari atualizados.'
      : !gravacao.pode
        ? gravacao.faltam.length
          ? `Para gravar, falta o consentimento de: ${gravacao.faltam.join(', ')}.`
          : 'Marque quem está presente e o consentimento de cada um.'
        : null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-ha-text">
          <span
            aria-hidden
            className={`inline-block h-2.5 w-2.5 rounded-full ${gravando ? 'animate-pulse bg-red-500' : g.estado === 'fora' ? 'bg-ha-muted' : 'border border-ha-muted'}`}
          />
          {gravando ? 'Gravando' : g.estado === 'fora' ? 'Fora da gravação' : 'Sem gravar'}
          <span className="font-normal text-ha-muted">
            · {relogio(gravacao.segundos + naFatia)} gravados
            {g.pendentes ? ` · ${g.pendentes} ${g.pendentes === 1 ? 'fatia subindo' : 'fatias subindo'}` : ''}
          </span>
        </p>
        <div className="flex flex-wrap gap-2">
          {g.estado === 'parado' || g.estado === 'sem_microfone' ? (
            <button
              type="button"
              disabled={!!bloqueio}
              onClick={() => (gravacao.fora_da_gravacao ? g.voltarAGravar() : g.gravar())}
              className="rounded-element bg-red-600 px-3 py-1.5 text-[10px] font-bold uppercase text-white disabled:opacity-40"
            >
              Gravar
            </button>
          ) : null}
          {gravando ? (
            <>
              <button
                type="button"
                onClick={() => g.sairDaGravacao()}
                className="rounded-element border border-ha-border2 px-3 py-1.5 text-[10px] font-bold uppercase text-ha-text"
              >
                Fora da gravação
              </button>
              <button
                type="button"
                onClick={() => g.parar()}
                className="rounded-element border border-ha-border2 px-3 py-1.5 text-[10px] font-bold uppercase text-ha-text"
              >
                Parar
              </button>
            </>
          ) : null}
          {g.estado === 'fora' ? (
            <button
              type="button"
              disabled={!!bloqueio}
              onClick={() => g.voltarAGravar()}
              className="rounded-element bg-red-600 px-3 py-1.5 text-[10px] font-bold uppercase text-white disabled:opacity-40"
            >
              Voltar a gravar
            </button>
          ) : null}
        </div>
      </div>
      {bloqueio && !gravando ? <p className="text-xs text-ha-muted">{bloqueio}</p> : null}
      {g.estado === 'fora' ? (
        <p className="text-xs text-ha-muted">
          Nada está sendo gravado. O app guarda só que houve um trecho fora da gravação, e quando.
        </p>
      ) : null}
      {g.erro ? <p className="text-xs text-red-600">{g.erro}</p> : null}
    </div>
  );
}
